<template>
  <div>
    <v-container class="container px-0">
      <v-row
        class="title-row"
        :class="`${
          $vuetify.breakpoint.mdAndDown ? 'flex-column flex-nowrap' : ''
        }`"
      >
        <v-col class="d-flex align-center">
          <h4 class="font-weight-light">{{ $t('heading.hosting.sftp.title') }}</h4>
          <active-server-account-selector
            v-if="service && service.server_accounts.length > 1"
            class="ml-4"
            :value.sync="serverAccount"
            :items="service.server_accounts"
          />
        </v-col>

        <v-col
          v-show="items.length"
          class="page-title__filter-controls justify-end"
          :class="[`${$vuetify.breakpoint.mobile ? 'pb-3' : ''}`]"
        >
          <data-iterator-filter-controls
            :keys="keys"
            :showSearchTerm="true"
            :searchTerm="searchTerm"
            :fullWidthSearch="false"
            :fullWidthLg="false"
            :fullWidthMd="true"
            @update:sortBy="changeSortBy"
            @update:sortDesc="handleSortDescChange"
            @update:searchTerm="handleSearchTermChange"
            :class="'my-hostings-search'"
          ></data-iterator-filter-controls>

          <quick-actions-button
            :icon="false"
            :label="$t('button.actions')"
            :mobileIcon="`settings`"
            :hideCustomizeOption="true"
            :actions="actions"
          />

          <v-btn
            :large="!$vuetify.breakpoint.smAndDown"
            elevation="0"
            color="primary"
            class="p-2 add-new-button page-title__add-button"
            @click="showAddModal"
            :loading="addButtonLoading"
          >
            <v-icon :class="$vuetify.breakpoint.mobile ? 'mr-0' : ''"
              >$plus
            </v-icon>
            {{ $vuetify.breakpoint.mobile ? "" : $t('button.sftp.add') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-container class="mt-3 px-0">
      <v-row>
        <v-col cols="12">
          <div :class="checkedItems.length > 0 ? 'pb-15' : ''">
            <hosting-ftp-table
              v-if="serverAccount && !serverAccount.diagnostic_mode"
              :checkedItems="checkedItems"
              :headers="headers"
              :items="items"
              :itemsLoading="itemsLoading"
              :itemsPerPage="itemsPerPage"
              :itemsTotal="itemsTotal"
              :page="page"
              :possibleItemsPerPage="[[8, 16, 24]]"
              :searchTerm="searchTerm"
              :sortBy="sortBy"
              :sortDesc="sortDesc"
              @clearFilters="searchTerm = ''"
              @update:check-all="handleSelectAll"
              @update:checked-items="handleItemCheck"
              @update:itemsPerPage="changePerPage"
              @update:page="changePage"
              @update:sortBy="changeSortBy"
              @update:sortDesc="changeSortDesc"
              @action-button-info="loadInfoModal"
              @action-button-edit="showEditModal"
              @action-button-delete="showDeleteModal"
              :itemClass="itemClass"
              :usageSupported="usageSupportedByServer"
            >
              <template v-slot:no-data>
                <TableFullScreenMessage
                  :title="$t('message.emptyTable.sftp.title')"
                  :desc="$t('message.emptyTable.sftp.description')"
                >
                  <template v-slot:image>
                    <hosting-ftp-illustration />
                  </template>
                  <template v-slot:action>
                    <v-btn
                      :large="!$vuetify.breakpoint.smAndDown"
                      :small="$vuetify.breakpoint.smAndDown"
                      elevation="0"
                      color="primary"
                      class="p-2 add-new-button"
                      @click="showAddModal"
                    >
                      <v-icon :class="$vuetify.breakpoint.mobile ? 'mr-0' : ''"
                        >$plus
                      </v-icon>
                      {{ $vuetify.breakpoint.mobile ? "" : $t('button.sftp.add') }}
                    </v-btn>
                  </template>
                </TableFullScreenMessage>
              </template>
              <template v-slot:updated_at="item">
                <span>{{ item.updated_at_text }}</span>
              </template>
            </hosting-ftp-table>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      transition="custom-dialog-transition"
      :persistent="modalOptions.persistent"
      v-model="modalOptions.open"
      ref="dialog"
    >
      <div
        class="card-overlay"
        v-if="!modalOptions.persistent"
        @click="modalOptions.open = !modalOptions.open"
      />
      <div class="card-overlay" v-else @click="$refs.dialog.animateClick()" />
      <basic-modal
        style="width: 560px"
        :modalOptions="modalOptions"
        @modal-close="modalClose"
        :key="modalRender"
      />
    </v-dialog>
  </div>
</template>

<script>
import Api from "../../apis/Api";

import BasicModal from "@/components/modal/BasicModal";
import DataIteratorFilterControls from "../../components/dataIterator/DataIteratorFilterControls.vue";
import HostingFtpTable from "../../components/dataIterator/tables/HostingFtpTable.vue";
import TableFullScreenMessage from "../../components/dataIterator/tables/TableFullScreenMessage.vue";
import HostingFtpIllustration from "../../components/illustrations/hosting/hosting-ftp-illustration.vue";

import ActionModalMixin from "@/mixins/ActionModalMixin";
import DataIteratorPageMixin from "../../mixins/DataIteratorPageMixin";
import CustomTablePageMixin from "../../mixins/CustomTablePageMixin";
import moment from "moment";
import ActiveServerAccountSelector from "@/components/ActiveServerAccountSelector.vue";
import QuickActionsButton from "../../components/QuickActionsButton.vue";

export default {
  components: {
    QuickActionsButton,
    ActiveServerAccountSelector,
    BasicModal,
    DataIteratorFilterControls,
    HostingFtpTable,
    HostingFtpIllustration,
    TableFullScreenMessage,
  },
  mixins: [ActionModalMixin, DataIteratorPageMixin, CustomTablePageMixin],
  props: {
    service: Object
  },
  data() {
    return {
      isServerProvided: false,
      loading: true,
      addButtonLoading: false,
      items: [],
      usageSupportedByServer: false,
      searchTerm: "",
      headers: [
        {
          text: 'user',
          value: "user",
          sortable: true,
        },
        {
          text: 'updated',
          value: "updated_at",
          sortable: true,
        },
        {
          text: 'actions',
          value: "actions",
        },
      ],
      sortBy: "updated_at",
      sortDesc: true,
      page: 1,
      itemsPerPage: 8,
      itemsLoading: true,
      modalRender: 0,
      modalOptions: { open: false },
      highlightItem: {},
      authMethods: [
        { label: this.$t('form.option.password'), value: "password" },
        { label: this.$t('form.option.sshKey'), value: "public_key" },
      ],
      serverAccount: null,
      accessType: null
    };
  },
  watch: {
    highlightItem: function (newValue) {
      if (!newValue) return;
      setTimeout(() => {
        this.highlightItem = {};
      }, 1000);
    },
    service: function () {
      this.serverAccount = this.service.server_accounts[0];
    },
    serverAccount: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.reloadData();
      }
    },
    "modalOptions.open": function (value) {
      value
        ? this.$store.dispatch("lockBodyScroll")
        : this.$store.dispatch("unlockBodyScroll");
    },
  },

  methods: {
    reloadData() {
      if (this.serverAccount.diagnostic_mode) {
        this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: this.$t("message.hostingAccountInDiagnosticMode"),
        });
        this.itemsLoading = false;
        return;
      }

      this.loading = true;
      Api.get(`/server-accounts/${this.serverAccount.id}/sftp-accounts`)
        .then((response) => {
          this.items = response.data.data.map((item) => {
            item.updated_at_text = item.updated_at
              ? moment(item.updated_at).fromNow()
              : "-";
            item.infoButtonLoading = false;
            return item;
          });
          this.accessType = response.data.meta.access_type;
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.itemsLoading = false;
        });
    },
    showAddModal() {
      this.resetModal();
      this.modalOptions.title = this.$t('heading.hosting.sftp.modal.add.title')
      this.modalOptions.icon = "$plus";
      this.modalOptions.buttons.unshift({
        label: this.$t('button.sftp.add'),
        color: "primary",
        onclick: (modal) => {
          modal.$refs.form.validate() && this.addSftpUser(modal.formValues);
        },
      });
      this.modalOptions.formFields = [
        {
          label: this.$t('form.label.username'),
          name: "username",
          tooltip: "tooltip.add_sftp_account.username",
          type: "text",
          prefix: this.serverAccount.details.remote_id + '-',
        },
        {
          label: this.$t('form.label.authenticationMethod'),
          name: "type",
          tooltip: "tooltip.add_sftp_account.type",
          type: "select",
          options: this.authMethods,
          required: true,
          onChange: (values) => {
            if (values.type == "password") {
              for (const field of this.modalOptions.formFields) {
                if (field.name == "public_key") {
                  field.hidden = true;
                }
                if (field.name == "password") {
                  field.hidden = false;
                }
              }
            } else {
              for (const field of this.modalOptions.formFields) {
                if (field.name == "public_key") {
                  field.hidden = false;
                }
                if (field.name == "password") {
                  field.hidden = true;
                }
              }
            }
          },
        },
        {
          label: this.$t('form.label.password'),
          name: "password",
          tooltip: "tooltip.add_sftp_account.password",
          type: "generatePassword",
          hidden: false
        },
        {
          label: this.$t('form.label.public_key'),
          name: "public_key",
          tooltip: "tooltip.add_sftp_account.public_key",
          type: "text",
          hidden: true
        },
      ];
      this.modalOptions.item = {
        type: this.authMethods[0].value
      };
      this.modalOptions.open = true;
    },
    addSftpUser(formData) {
      this.modalOptions.persistent = true;
      this.modalOptions.submitting = true;
      this.modalOptions.submittingSuccess = "";
      this.modalOptions.submittingError = "";

      if (formData.unlimited_quota) {
        formData.quota = 0;
      }
      Api.post(
        `/server-accounts/${this.serverAccount.id}/sftp-accounts`,
        formData
      )
        .then(() => {
          this.modalOptions.submittingSuccess = this.$t('notification.hosting.sftp.add.success');
          this.highlightItem = { user: `${formData.user}@${formData.domain}` };
          this.reloadData();

          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.modalOptions.submittingSuccess,
          });
          this.modalOptions.open = false;
        })
        .catch((error) => {
          this.modalOptions.submittingError = Api.getErrorMessage(error);

          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: this.modalOptions.submittingError,
          });
        })
        .finally(() => {
          this.modalOptions.persistent = false;
          this.modalOptions.submitting = false;
        });
    },
    loadInfoModal(item) {
      item.infoButtonLoading = true;
      Api.cached()
        .get(
          `/server-accounts/${this.serverAccount.id}/sftp-accounts/server-info`
        )
        .then((response) => {
          this.showInfoModal(item, response.data.data);
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          item.infoButtonLoading = false;
        });
    },
    showInfoModal(item, info) {
      this.resetModal();
      this.modalOptions.item = item;
      this.modalOptions.title = this.$t('heading.hosting.sftp.modal.show.title', {user: `<span style="text-wrap: nowrap">${item.user}</span>`});
      this.modalOptions.icon = "$help";

      this.modalOptions.message = "<ul>";
      for (const [key, value] of Object.entries({
        "username": item.user,
        "host": info.host,
        "port": info.port,
      })) {
        const field = this.$t('heading.hosting.sftp.modal.show.info.' + key);
        this.modalOptions.message += `<li><strong style="display:inline-block">${field}:</strong> <span style="display:inline-block">${value}</span></li>`;
      }
      this.modalOptions.message += "</ul>";

      this.modalOptions.open = true;
    },
    showEditModal(item) {
      this.resetModal();
      this.modalOptions.item = item;
      this.modalOptions.title = this.$t('heading.hosting.sftp.modal.update.title')
      this.modalOptions.icon = "$edit";
      this.modalOptions.message = `<b>${this.$t('heading.hosting.sftp.modal.update.info')}:</b> ${item.user}`;

      this.modalOptions.buttons.unshift({
        label: this.$t('button.update'),
        color: "primary",
        onclick: (modal) => {
          modal.$refs.form.validate() &&
            this.updateSFtpAccount(item, modal.formValues);
        },
      });

      this.modalOptions.formFields = [
        {
          label: this.$t('form.label.password'),
          tooltip: "tooltip.edit_sftp_account.password",
          name: "hiddenPassword",
          type: "hiddenPassword",
          onButtonClick: () => {
            for (const field of this.modalOptions.formFields) {
              if (field.name == "hiddenPassword") {
                field.hidden = true;
              }
              if (field.name == "password") {
                field.hidden = false;
              }
            }
          },
        },
        {
          label: this.$t('form.label.password'),
          tooltip: "tooltip.edit_sftp_account.password",
          name: "password",
          type: "generatePassword",
          hidden: true,
        },
        {
          label: this.$t('form.label.public_key'),
          name: "public_key",
          tooltip: "tooltip.edit_sftp_account.public_key",
          type: "text"
        },
      ];
      this.modalOptions.open = true;
    },
    updateSFtpAccount(item, formData) {
      this.modalOptions.persistent = true;
      this.modalOptions.submitting = true;
      this.modalOptions.submittingSuccess = "";
      this.modalOptions.submittingError = "";

      if (formData.unlimited_quota) {
        formData.quota = 0;
      }
      Api.put(
        `/server-accounts/${this.serverAccount.id}/sftp-accounts/${item.user}`,
        formData
      )
        .then(() => {
          this.modalOptions.submittingSuccess = this.$t('notification.hosting.sftp.update.success');
          this.highlightItem = item;
          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.modalOptions.submittingSuccess,
          });
          this.reloadData();
          this.modalOptions.open = false;
        })
        .catch((error) => {
          this.modalOptions.submittingError = Api.getErrorMessage(error);

          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: this.modalOptions.submittingError,
          });
        })
        .finally(() => {
          this.modalOptions.persistent = false;
          this.modalOptions.submitting = false;
        });
    },
    showDeleteModal(item) {
      this.resetModal();
      this.modalOptions.item = item;
      this.modalOptions.title = this.$t('heading.hosting.sftp.modal.delete.title')
      this.modalOptions.color = "error";
      this.modalOptions.icon = "$alertwarning";

      this.modalOptions.formFields = [
        {
          message: `<b>${this.$t('message.confirmAction')}</b>`,
          label: this.$t('form.confirmDelete.sftp'),
          name: "confirm",
          type: "checkbox",
          required: true,
        },
      ];
      this.modalOptions.buttons.unshift({
        label: this.$t('button.delete'),
        color: "error",
        onclick: (modal) => {
          modal.$refs.form.validate() && this.deleteAccount(item);
        },
      });
      this.modalOptions.open = true;
    },
    deleteAccount(item) {
      this.modalOptions.persistent = true;
      this.modalOptions.submitting = true;
      this.modalOptions.submittingSuccess = "";
      this.modalOptions.submittingError = "";

      Api.delete(
        `/server-accounts/${this.serverAccount.id}/sftp-accounts/${item.user}`
      )
        .then(() => {
          this.modalOptions.submittingSuccess = this.$t('notification.hosting.sftp.delete.success');
          this.reloadData();

          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.modalOptions.submittingSuccess,
          });
          this.modalOptions.open = false;
        })
        .catch((error) => {
          this.modalOptions.submittingError = Api.getErrorMessage(error);

          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: this.modalOptions.submittingError,
          });
        })
        .finally(() => {
          this.modalOptions.persistent = false;
          this.modalOptions.submitting = false;
        });
    },
    showDisconnectAllModal() {
      this.resetModal();
      this.modalOptions.title = this.$t('heading.hosting.sftp.modal.disconnect.title')
      this.modalOptions.message = this.$t('heading.hosting.sftp.modal.disconnect.message')
      this.modalOptions.color = "primary";
      this.modalOptions.icon = "$logout";

      this.modalOptions.buttons.unshift({
        label: this.$t('button.disconnect.all'),
        color: "primary",
        onclick: () => {
          this.disconnectAll();
        },
      });
      this.modalOptions.open = true;
    },
    disconnectAll() {
      this.modalOptions.persistent = true;
      this.modalOptions.submitting = true;
      this.modalOptions.submittingSuccess = "";
      this.modalOptions.submittingError = "";

      Api.post(
        `/server-accounts/${this.serverAccount.id}/sftp-accounts/disconnect-all`
      )
        .then(() => {
          this.modalOptions.submittingSuccess = this.$t('notification.hosting.sftp.disconnect.success');

          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.modalOptions.submittingSuccess,
          });
          this.modalOptions.open = false;
        })
        .catch((error) => {
          this.modalOptions.submittingError = Api.getErrorMessage(error);

          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: this.modalOptions.submittingError,
          });
        })
        .finally(() => {
          this.modalOptions.persistent = false;
          this.modalOptions.submitting = false;
        });
    },
    showSetAccessMethodModal() {
      this.resetModal();
      this.modalOptions.title = this.$t('heading.hosting.sftp.modal.manageSSH.title')
      this.modalOptions.message = this.$t('heading.hosting.sftp.modal.manageSSH.message')
      this.modalOptions.color = "primary";
      this.modalOptions.icon = "$settings";

      this.modalOptions.formFields = [
        {
          label: this.$t('heading.hosting.sftp.modal.manageSSH.field'),
          name: "access_type",
          type: "select",
          tooltip: "tooltip.hosting.sftp.accessMethod",
          options: [
            {
              label: this.$t('general.ssh'),
              value: 'ssh'
            },
            {
              label: this.$t('general.sftp'),
              value: 'sftp'
            },
          ]
        },
      ]

      this.modalOptions.buttons.unshift({
        label: this.$t('button.setAccessMethod'),
        color: "primary",
        onclick: (modal) => {
          this.setAccessMethod(modal.formValues);
        },
      });

      this.modalOptions.item = {
        access_type: this.accessType,
      };
      this.modalOptions.open = true;
    },
    setAccessMethod(item) {
      Api.post(
        `/server-accounts/${this.serverAccount.id}/sftp-accounts/set-access-type`,
        {
          access_type: item.access_type
        }
      )
        .then(() => {
          this.modalOptions.submittingSuccess = this.$t('notification.hosting.sftp.setAccessType.success');

          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.modalOptions.submittingSuccess,
          });
          this.modalOptions.open = false;
        })
        .catch((error) => {
          this.modalOptions.submittingError = Api.getErrorMessage(error);

          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: this.modalOptions.submittingError,
          });
        })
        .finally(() => {
          this.modalOptions.persistent = false;
          this.modalOptions.submitting = false;
        });
    },
    itemClass(item) {
      if (this.highlightItem.user && item.user == this.highlightItem.user) {
        return "highlight";
      }
    },
  },
  mounted() {
    if (this.service) {
      this.serverAccount = this.service.server_accounts[0];
    }
  },
  computed: {
    itemsTotal: function () {
      return this.items.length;
    },
    actions() {
      let actions = [
        {
          title: this.$t('button.disconnect.all'),
          iconSize: "20",
          icon: "$logout",
          action: this.showDisconnectAllModal,
        },
      ];

      if (this.service.plan_settings.allow_user_enable_ssh_access) {
        actions.push({
          title: this.$t('button.manageSshAccess'),
          iconSize: "20",
          icon: "$settings",
          action: this.showSetAccessMethodModal,
        })
      }

      return actions
    }
  },
};
</script>

<style scoped lang="scss">
.title-row {
  @media (min-width: 1401px) {
    height: 70px;
  }
}
</style>
